import { type LeadTrackAssignment, resolveTrackContent } from 'lib/lead-tracks';
import type { MissiveComposeErrorDetail } from 'lib/missive/compose-errors';
import type { EmailComposePayload } from 'lib/missive/email-compose';
import { openMissiveInNewTab } from 'lib/missive/open-url';
import { useCallback, useEffect, useState } from 'react';

import LeadEmailComposeModal from 'components/leads/lead-email-compose-modal';

import { useSession } from 'providers/session';
import { useUI } from 'providers/ui';

type OpenMissiveEmailOptions = {
  leadId?: string;
  name?: string;
};

type ComposeModalState = {
  email: string;
  leadId?: string;
  name?: string;
};

function readStoredMissiveOpenError() {
  if (typeof window === 'undefined') {
    return null;
  }

  const raw = localStorage.getItem('cc-missive-open-error');

  if (!raw) {
    return null;
  }

  try {
    const parsed = JSON.parse(raw) as MissiveComposeErrorDetail & {
      createdAt?: number;
    };

    if (!parsed.createdAt || Date.now() - parsed.createdAt > 2 * 60 * 1000) {
      localStorage.removeItem('cc-missive-open-error');
      return null;
    }

    localStorage.removeItem('cc-missive-open-error');
    return parsed;
  } catch {
    localStorage.removeItem('cc-missive-open-error');
    return null;
  }
}

export function useOpenMissiveEmail() {
  const { token } = useSession();
  const ui = useUI();
  const [opening, setOpening] = useState(false);
  const [composeModal, setComposeModal] = useState<ComposeModalState | null>(
    null,
  );

  useEffect(() => {
    function handleMissiveOpenError(event: MessageEvent) {
      if (event.origin !== window.location.origin) {
        return;
      }

      if (event.data?.type !== 'cc-missive-open-error') {
        return;
      }

      ui.setToast(true, {
        type: 'error',
        title: event.data.title || 'Could not open Missive email',
        message: [event.data.message, event.data.hint]
          .filter(Boolean)
          .join(' '),
      });
    }

    window.addEventListener('message', handleMissiveOpenError);

    const storedError = readStoredMissiveOpenError();

    if (storedError) {
      ui.setToast(true, {
        type: 'error',
        title: storedError.title || 'Could not open Missive email',
        message: [storedError.message, storedError.hint]
          .filter(Boolean)
          .join(' '),
      });
    }

    return () => {
      window.removeEventListener('message', handleMissiveOpenError);
    };
  }, [ui]);

  const openMissiveEmail = useCallback(
    (email: string, options?: OpenMissiveEmailOptions | string) => {
      const address = email.trim();
      const resolvedOptions =
        typeof options === 'string' ? { name: options } : options || {};

      if (!address) {
        return;
      }

      if (!token) {
        window.location.href = `mailto:${address}`;
        return;
      }

      setComposeModal({
        email: address,
        leadId: resolvedOptions.leadId,
        name: resolvedOptions.name,
      });
    },
    [token],
  );

  const closeComposeModal = useCallback(() => {
    setComposeModal(null);
  }, []);

  const launchMissive = useCallback(
    (emailCompose: EmailComposePayload) => {
      if (!token || !composeModal) {
        return;
      }

      setOpening(true);

      openMissiveInNewTab(
        {
          email: composeModal.email,
          name: composeModal.name,
          leadId: composeModal.leadId,
          emailCompose,
        },
        token,
      );

      setComposeModal(null);
      setOpening(false);
    },
    [composeModal, token],
  );

  const submitComposeModal = useCallback(
    (emailCompose: EmailComposePayload) => {
      launchMissive(emailCompose);
    },
    [launchMissive],
  );

  const openExistingConversation = useCallback(() => {
    launchMissive({ mode: 'existing' });
  }, [launchMissive]);

  const EmailComposeModal = composeModal ? (
    <LeadEmailComposeModal
      open={Boolean(composeModal)}
      email={composeModal.email}
      leadName={composeModal.name}
      onClose={closeComposeModal}
      onOpenExisting={openExistingConversation}
      onSubmit={submitComposeModal}
    />
  ) : null;

  return {
    openMissiveEmail,
    openingMissiveEmail: opening,
    EmailComposeModal,
  };
}
